/**
 * Kuerbis — Druide (Kuerbius, Naturmagier). LMAO-Style (BATTLE-17):
 * "gemuetlicher Herbst-Magier" — satte orange Kugel mit deutlichen VERTIKALEN
 * Rippen (dunkle Furchen + Licht-Wulste, Lichtquelle oben-links), gelappte
 * Silhouette ueber Seiten-Lappen hinter dem Hauptkorpus, oben kurzer gruener
 * Stiel mit Ranke + Blatt (Natur-Motiv).
 * castPulse: Mondfeuer — Ranke + Furchen gluehen in KEYWORD-Brand-Orange.
 * 2★: Efeu-Ranke wickelt sich um den Bauch (Natur-Ruestung) + zweites Blatt.
 * 3★: gluehende Mondsichel-Rune auf der Stirn + permanente Furchen-Glut.
 * Gesichts-Zonen (renderer.ts): Augen y-0.18r, Mund y+0.3r — beides bleibt frei.
 * Referenz-Qualitaet: kohlrabi.ts / okra.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, rimLight, specular, volumeGradient } from '../draw3d'
import { KEYWORD_COLORS } from '../../ui/theme'

/** verbindliche Brand-Farbe (docs/12 §2) — Mondfeuer-Glow, nie Koerperfarbe */
const MOONFIRE = KEYWORD_COLORS.burn

export const paintKuerbis: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  const outlineW = Math.max(2, r * 0.12)
  const cast = dead ? 0 : pose.castPulse

  ctx.save()
  ctx.translate(x, y)
  ctx.lineJoin = 'round'
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW

  // --- Stiel (hinter dem Korpus verankert): kurz, knubbelig, leicht gebogen ---
  ctx.save()
  const stemG = ctx.createLinearGradient(-r * 0.14, 0, r * 0.2, 0)
  stemG.addColorStop(0, lighten(colors.accent, 16))
  stemG.addColorStop(0.55, colors.accent)
  stemG.addColorStop(1, darken(colors.accent, 22))
  ctx.fillStyle = stemG
  ctx.beginPath()
  ctx.moveTo(-r * 0.14, -r * 0.74)
  ctx.bezierCurveTo(-r * 0.13, -r * 1.0, -r * 0.06, -r * 1.16, r * 0.0, -r * 1.24)
  ctx.lineTo(r * 0.17, -r * 1.18)
  ctx.bezierCurveTo(r * 0.12, -r * 1.06, r * 0.15, -r * 0.92, r * 0.18, -r * 0.74)
  ctx.closePath()
  ctx.fill()
  ctx.stroke()
  // hellere Schnittflaeche oben (wie Kohlrabi-Stummel-Deckel)
  ctx.fillStyle = lighten(colors.accent, 26)
  ctx.lineWidth = Math.max(1, r * 0.05)
  ctx.beginPath()
  ctx.ellipse(r * 0.085, -r * 1.2, r * 0.085, r * 0.045, -0.32, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()
  ctx.restore()

  // --- Ranke (Natur-Motiv): Bogen nach rechts mit Spiral-Ende, aus der Silhouette ---
  const tendril = (): void => {
    ctx.beginPath()
    ctx.moveTo(r * 0.14, -r * 0.98)
    ctx.bezierCurveTo(r * 0.34, -r * 1.18, r * 0.52, -r * 1.2, r * 0.6, -r * 1.06)
    ctx.arc(r * 0.52, -r * 1.02, r * 0.09, -0.45, Math.PI * 1.35)
  }
  ctx.save()
  ctx.lineCap = 'round'
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(2, r * 0.115)
  tendril()
  ctx.stroke()
  if (cast > 0.05) {
    // Mondfeuer: die Ranke glueht als Cast-Antenne (Brief: "Ranken-Akzent glueht")
    ctx.shadowColor = MOONFIRE
    ctx.shadowBlur = r * 0.3 * cast
    ctx.strokeStyle = rgba(MOONFIRE, 0.35 + cast * 0.6)
    ctx.lineWidth = Math.max(1.6, r * 0.09)
    tendril()
    ctx.stroke()
    ctx.shadowBlur = 0
  }
  ctx.strokeStyle = dead ? colors.accent : lighten(colors.accent, 12)
  ctx.lineWidth = Math.max(1.2, r * 0.055)
  tendril()
  ctx.stroke()
  ctx.restore()

  // --- Blatt/Blaetter am Stielansatz (links; 2★ zusaetzlich rechts) ---
  const leaf = (lx: number, ly: number, ang: number, len: number): void => {
    ctx.save()
    ctx.translate(lx, ly)
    ctx.rotate(ang)
    const lg = ctx.createLinearGradient(0, 0, 0, -len)
    lg.addColorStop(0, darken(colors.accent, 14))
    lg.addColorStop(1, lighten(colors.accent, 22))
    ctx.fillStyle = lg
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.2, r * 0.05)
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.quadraticCurveTo(-len * 0.62, -len * 0.34, 0, -len)
    ctx.quadraticCurveTo(len * 0.62, -len * 0.34, 0, 0)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    // Blattader (Licht-Kante)
    ctx.strokeStyle = rgba(lighten(colors.accent, 38), dead ? 0.35 : 0.85)
    ctx.lineWidth = Math.max(1, r * 0.028)
    ctx.beginPath()
    ctx.moveTo(0, -len * 0.12)
    ctx.quadraticCurveTo(-len * 0.06, -len * 0.5, 0, -len * 0.86)
    ctx.stroke()
    ctx.restore()
  }
  leaf(-r * 0.16, -r * 0.98, -0.7, r * 0.42)
  if (tier >= 2) leaf(r * 0.3, -r * 0.92, 0.85, r * 0.32)

  // --- Seiten-Lappen (hinter dem Hauptkorpus): gelappte Kuerbis-Silhouette ---
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  for (const s of [-1, 1]) {
    ctx.fillStyle = volumeGradient(ctx, s * r * 0.56 - r * 0.1, -r * 0.15, r * 0.72, darken(colors.body, 8))
    ctx.beginPath()
    ctx.ellipse(s * r * 0.56, r * 0.03, r * 0.4, r * 0.74, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
  }

  // --- Hauptkorpus: rund und gemuetlich aber massiv, mit Volumen.
  //     Outline NUR ausserhalb der Lappen (evenodd-Clip) — sonst entsteht ein
  //     dunkler "Kaefig" ueber dem Orange; die Naht zu den Lappen wird weiter
  //     unten als Furche (Body-Ton) gezeichnet, nicht als harte Outline. ---
  ctx.fillStyle = volumeGradient(ctx, 0, 0, r, colors.body, 22, 20)
  ctx.beginPath()
  ctx.ellipse(0, 0, r * 0.74, r * 0.9, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.save()
  ctx.beginPath()
  ctx.rect(-r * 2, -r * 2, r * 4, r * 4)
  // moveTo auf den Arc-Start jeder Ellipse — sonst zieht ellipse() eine
  // Verbindungslinie vom aktuellen Punkt und kippt die evenodd-Paritaet
  ctx.moveTo(-r * 0.16, r * 0.03)
  ctx.ellipse(-r * 0.56, r * 0.03, r * 0.4, r * 0.74, 0, 0, Math.PI * 2)
  ctx.moveTo(r * 0.96, r * 0.03)
  ctx.ellipse(r * 0.56, r * 0.03, r * 0.4, r * 0.74, 0, 0, Math.PI * 2)
  ctx.clip('evenodd')
  ctx.beginPath()
  ctx.ellipse(0, 0, r * 0.74, r * 0.9, 0, 0, Math.PI * 2)
  ctx.stroke()
  ctx.restore()

  // Delle am Stielansatz (Kuerbis-Charakteristik)
  ctx.fillStyle = rgba(darken(colors.body, 26), 0.55)
  ctx.beginPath()
  ctx.ellipse(r * 0.01, -r * 0.8, r * 0.17, r * 0.055, 0, 0, Math.PI * 2)
  ctx.fill()

  // --- Vertikale Rippen auf dem Hauptkorpus (geclippt): dunkle Furche
  //     unten-rechts + Licht-Wulst zur Lichtquelle oben-links.
  //     Nur 2 Innen-Furchen — die Zone ums Gesicht (y-r*0.18) bleibt ruhig;
  //     die Korpus-Kanten ueber den Seiten-Lappen bilden Furchen 3+4. ---
  const furrow = (s: number, ox: number, oy: number): void => {
    ctx.beginPath()
    ctx.moveTo(s * r * 0.07 + ox, -r * 0.76 + oy)
    ctx.bezierCurveTo(
      s * r * 0.56 + ox, -r * 0.42 + oy,
      s * r * 0.56 + ox, r * 0.44 + oy,
      s * r * 0.09 + ox, r * 0.8 + oy,
    )
  }
  // Naht-Furchen zu den Seiten-Lappen: Arcs entlang der Korpus-Kante
  // (zwei getrennte Strokes — ellipse() nach moveTo wuerde Verbinder ziehen)
  const seam = (shrink: number): void => {
    for (const a0 of [-1.0, Math.PI - 1.0]) {
      ctx.beginPath()
      ctx.ellipse(0, 0, r * 0.74 - shrink, r * 0.9 - shrink, 0, a0, a0 + 2.0)
      ctx.stroke()
    }
  }
  ctx.save()
  ctx.beginPath()
  ctx.ellipse(0, 0, r * 0.74, r * 0.9, 0, 0, Math.PI * 2)
  ctx.clip()
  ctx.lineCap = 'round'
  for (const s of [-1, 1]) {
    ctx.strokeStyle = rgba(darken(colors.body, 30), 0.75)
    ctx.lineWidth = Math.max(1.2, r * 0.06)
    furrow(s, r * 0.02, r * 0.02)
    ctx.stroke()
    ctx.strokeStyle = rgba(lighten(colors.body, 28), dead ? 0.3 : 0.7)
    ctx.lineWidth = Math.max(1, r * 0.04)
    furrow(s, -r * 0.03, -r * 0.03)
    ctx.stroke()
  }
  ctx.strokeStyle = rgba(darken(colors.body, 28), 0.7)
  ctx.lineWidth = Math.max(1.2, r * 0.055)
  seam(r * 0.03)
  ctx.strokeStyle = rgba(lighten(colors.body, 28), dead ? 0.25 : 0.6)
  ctx.lineWidth = Math.max(1, r * 0.035)
  seam(r * 0.1)
  // Mondfeuer-Glut in den Furchen (Cast; 3★ glimmt permanent)
  const ember = Math.min(1, cast * 0.85 + (tier >= 3 && !dead ? 0.22 : 0))
  if (ember > 0.05) {
    ctx.shadowColor = MOONFIRE
    ctx.shadowBlur = r * (0.1 + 0.25 * ember)
    ctx.strokeStyle = rgba(MOONFIRE, ember)
    ctx.lineWidth = Math.max(1, r * 0.04)
    for (const s of [-1, 1]) {
      furrow(s, r * 0.02, r * 0.02)
      ctx.stroke()
    }
    seam(r * 0.03)
    ctx.shadowBlur = 0
  }
  ctx.restore() // Clip Ende

  // --- 2★: Efeu-Ranke um den Bauch (Natur-Ruestung) mit Licht-Kante + Blaettchen ---
  if (tier >= 2) {
    ctx.save()
    // auf die Gesamt-Silhouette clippen (Hauptkorpus + beide Lappen);
    // je Ellipse eigener Subpfad via moveTo (keine Verbindungslinien)
    ctx.beginPath()
    ctx.moveTo(-r * 0.16, r * 0.03)
    ctx.ellipse(-r * 0.56, r * 0.03, r * 0.4, r * 0.74, 0, 0, Math.PI * 2)
    ctx.moveTo(r * 0.96, r * 0.03)
    ctx.ellipse(r * 0.56, r * 0.03, r * 0.4, r * 0.74, 0, 0, Math.PI * 2)
    ctx.moveTo(r * 0.74, 0)
    ctx.ellipse(0, 0, r * 0.74, r * 0.9, 0, 0, Math.PI * 2)
    ctx.clip()
    const vine = (ox: number, oy: number): void => {
      ctx.beginPath()
      ctx.moveTo(-r * 0.98 + ox, r * 0.26 + oy)
      ctx.quadraticCurveTo(ox, r * 0.82 + oy, r * 0.98 + ox, r * 0.44 + oy)
    }
    ctx.lineCap = 'round'
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(2, r * 0.1)
    vine(0, 0)
    ctx.stroke()
    ctx.strokeStyle = darken(colors.accent, 6)
    ctx.lineWidth = Math.max(1.5, r * 0.065)
    vine(0, 0)
    ctx.stroke()
    ctx.strokeStyle = rgba(lighten(colors.accent, 30), dead ? 0.3 : 0.8)
    ctx.lineWidth = Math.max(1, r * 0.028)
    vine(-r * 0.012, -r * 0.022)
    ctx.stroke()
    ctx.restore()
    // Blaettchen sitzen AUF der Ranke, zeigen nach innen-unten (bleiben in der Silhouette)
    leaf(-r * 0.43, r * 0.54, 2.35, r * 0.19)
    leaf(r * 0.45, r * 0.6, -2.35, r * 0.19)
  }

  // --- 3★: Mondsichel-Rune (Mondfeuer) auf der STIRN (Gesichts-Zonen bleiben
  //     frei) — dunkler Sockel -> Brand-Glow -> heller Kern (Okra-Pass-System),
  //     pulsiert beim Cast. Sichel offen nach rechts, leicht gekippt. ---
  if (tier >= 3 && !dead) {
    const pulse = 0.8 + cast * 0.2
    const moon = (R: number): void => {
      ctx.beginPath()
      ctx.arc(0, 0, R, Math.PI * 0.5, Math.PI * 1.5)
      ctx.arc(R * 0.35, 0, R * 0.92, Math.PI * 1.5, Math.PI * 0.5, true)
      ctx.closePath()
    }
    ctx.save()
    ctx.translate(r * 0.01, -r * 0.64)
    ctx.rotate(-0.3)
    ctx.fillStyle = rgba(darken(colors.body, 36), 0.9)
    moon(r * 0.14)
    ctx.fill()
    ctx.shadowColor = MOONFIRE
    ctx.shadowBlur = r * (0.18 + cast * 0.2)
    ctx.fillStyle = rgba(MOONFIRE, pulse)
    moon(r * 0.115)
    ctx.fill()
    ctx.shadowBlur = 0
    ctx.fillStyle = rgba(lighten(MOONFIRE, 32), pulse)
    moon(r * 0.08)
    ctx.fill()
    ctx.restore()
  }

  // --- Licht: Rim oben-links + Specular + Licht-Kante am linken Lappen ---
  if (!dead) {
    rimLight(ctx, 0, 0, r * 0.78, r * 0.92, 'rgba(255, 246, 225, 0.5)', Math.max(1.5, r * 0.07))
    ctx.save()
    ctx.strokeStyle = 'rgba(255, 246, 225, 0.35)'
    ctx.lineWidth = Math.max(1.2, r * 0.05)
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.ellipse(-r * 0.56, r * 0.03, r * 0.33, r * 0.66, 0, Math.PI * 0.95, Math.PI * 1.4)
    ctx.stroke()
    ctx.restore()
    specular(ctx, -r * 0.3, -r * 0.44, r * 0.15, 0.5)
    // sanfter Natur-Schimmer beim Cast (Mondfeuer sammelt sich im Bauch)
    if (cast > 0.05) glow(ctx, 0, r * 0.3, r * 0.6, MOONFIRE, cast * 0.18)
  }

  ctx.restore()
}
